// Admin Dashboard page component - Simplified
import React, { useState, useEffect } from 'react';
import { useAuthContext as useAuth } from '../contexts/AuthContext';
import { useAppStore } from '../store/app';
import { Header } from '../components/Header';
import { Button } from '../components/Common';
import { permissionChecks } from '../lib/auth/helpers';
import toast from 'react-hot-toast';

export const AdminDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { 
    terminals, checkpoints, lanes, laneTypes,
    loading, errors,
    fetchTerminals, fetchCheckpoints, fetchLanes
  } = useAppStore();

  const [activeTab, setActiveTab] = useState<'terminals' | 'checkpoints' | 'lanes' | 'monitoring'>('terminals');
  const [refreshing, setRefreshing] = useState(false);

  // Check if user has admin panel access using group-based permissions
  const hasAdminAccess = permissionChecks.canViewAdminMenu();

  useEffect(() => {
    if (!hasAdminAccess) {
      toast.error('Access denied. Admin privileges required.');
      return;
    }
    
    refreshData();
  }, [user, hasAdminAccess]);

  const refreshData = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        fetchTerminals(),
        fetchCheckpoints(),
        fetchLanes()
      ]);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setRefreshing(false);
    }
  };

  if (!hasAdminAccess) {
    return (
      <div className="min-h-screen bg-gray-900 text-white">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
            <p className="text-gray-400">Admin privileges required to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />
      
      {/* Admin Header */}
      <div className="bg-gray-800 px-6 py-4 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Admin Panel</h1>
            <p className="text-gray-400">System administration and management</p>
          </div>
          <Button 
            onClick={refreshData}
            loading={refreshing}
            size="sm"
          >
            Refresh All
          </Button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-gray-800 px-6 border-b border-gray-700">
        <nav className="flex space-x-8">
          {[
            { id: 'terminals', label: 'Terminals', count: terminals.length },
            { id: 'checkpoints', label: 'Checkpoints', count: checkpoints.length },
            { id: 'lanes', label: 'Lanes', count: lanes.length },
            { id: 'monitoring', label: 'Monitoring', count: 0 }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-2 bg-gray-700 text-gray-300 py-1 px-2 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Content Area */}
      <div className="p-6">
        {activeTab === 'terminals' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Terminals</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {terminals.map((terminal) => (
                <div key={terminal.terminal_id} className="bg-gray-800 p-4 rounded-lg">
                  <h3 className="font-medium">{terminal.terminal_name}</h3>
                  <p className="text-gray-400 text-sm">ID: {terminal.terminal_id}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'checkpoints' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Checkpoints</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {checkpoints.map((checkpoint) => (
                <div key={checkpoint.checkpoint_id} className="bg-gray-800 p-4 rounded-lg">
                  <h3 className="font-medium">{checkpoint.checkpoint_name}</h3>
                  <p className="text-gray-400 text-sm">Terminal ID: {checkpoint.terminal_id}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'lanes' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Lanes</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {lanes.map((lane) => (
                <div key={lane.lane_id} className="bg-gray-800 p-4 rounded-lg">
                  <h3 className="font-medium">Lane {lane.lane_id}</h3>
                  <p className="text-gray-400 text-sm">Checkpoint ID: {lane.checkpoint_id}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'monitoring' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">System Monitoring</h2>
            <div className="bg-gray-800 p-6 rounded-lg">
              <p className="text-gray-400">Monitoring dashboard coming soon...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
